import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useAuth } from "@bw/shared/hooks";
import type { TeamPerson } from "@/libs/staffApi";
import { listOrganizationOwners, promoteOrganizationOwner, removeOrganizationOwner, setPrimaryBillingAdmin } from "@/libs/organizationOwnersApi";

export function OrganizationOwnersPanel({ orgId, members }: { orgId: string; members: TeamPerson[] }) {
  const { user } = useAuth();
  const cache = useQueryClient();
  const queryKey = ["organization-owners", orgId, user?.uid];
  const owners = useQuery({ queryKey, queryFn: () => listOrganizationOwners(orgId), retry: false });
  const [memberId, setMemberId] = useState("");
  const [intent, setIntent] = useState<{ kind: "promote" | "remove" | "primary"; id: string; name: string } | null>(null);
  const busy = useRef(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eligible = members.filter(member => member.status === "active" && member.personal_user_id &&
    !owners.data?.some(owner => owner.user_id === member.personal_user_id));
  const selected = eligible.find(member => member.team_member_id === memberId);

  const confirm = async () => {
    if (!intent || busy.current) return;
    busy.current = true;
    setWorking(true);
    setError(null);
    try {
      if (intent.kind === "promote") await promoteOrganizationOwner(orgId, intent.id);
      else if (intent.kind === "primary") await setPrimaryBillingAdmin(orgId, intent.id);
      else await removeOrganizationOwner(orgId, intent.id);
      setIntent(null);
      setMemberId("");
      void cache.invalidateQueries({ queryKey });
      void cache.invalidateQueries({ queryKey: ["staff-people", orgId] });
      void cache.invalidateQueries({ queryKey: ["org", "me"] });
      void cache.invalidateQueries({ queryKey: ["org-finance"] });
    } catch {
      setError("We couldn’t confirm the owner change. Refresh the owner list before trying again.");
    } finally {
      busy.current = false;
      setWorking(false);
    }
  };

  return <Paper component="section" aria-label="Organization owners" variant="outlined" sx={{ p: 2 }}>
    <Stack spacing={2}>
      <Typography component="h2" variant="h6">Owners</Typography>
      <Typography variant="body2" color="text.secondary">Owners manage the organization. Company subscriptions and payment methods stay with the organization when owners change.</Typography>
      {owners.isPending ? <Typography role="status">Loading owners…</Typography> : owners.isError ? <Alert severity="error" action={<Button onClick={() => void owners.refetch()}>Retry</Button>}>Couldn’t load organization owners.</Alert> : <>
        {owners.data?.map(owner => {
          const person = members.find(member => member.personal_user_id === owner.user_id);
          const name = person?.display_name ?? (owner.user_id === user?.uid ? user.displayName || "You" : "Organization owner");
          return <Stack key={owner.user_id} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
            <Stack>
              <Typography>{name}{owner.user_id === user?.uid && name !== "You" ? " (you)" : ""}</Typography>
              {owner.is_primary_billing_admin ? <Typography color="text.secondary" variant="body2">Primary billing administrator</Typography> : null}
            </Stack>
            {!owner.is_primary_billing_admin && <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button disabled={working || owners.isFetching}
                onClick={() => setIntent({ kind: "primary", id: owner.user_id, name })}>Make primary billing admin</Button>
              <Button color="warning" disabled={working || owners.isFetching || (owners.data?.length ?? 0) <= 1}
                onClick={() => setIntent({ kind: "remove", id: owner.user_id, name })}>Remove owner role</Button>
            </Stack>}
          </Stack>;
        })}
        {eligible.length ? <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField select fullWidth label="Promote a team member" value={selected ? memberId : ""} disabled={working || owners.isFetching} onChange={event => setMemberId(event.target.value)}>
            {eligible.map(member => <MenuItem key={member.team_member_id} value={member.team_member_id}>{member.display_name}</MenuItem>)}
          </TextField>
          <Button disabled={!selected || working || owners.isFetching} onClick={() => selected && setIntent({ kind: "promote", id: selected.team_member_id, name: selected.display_name })}>Make owner</Button>
        </Stack> : members.some(member => !member.personal_user_id && (member.status === "active" || member.status === "pending")) ? <Typography variant="body2" color="text.secondary">A team member must accept their BW invitation before becoming an owner.</Typography> : null}
      </>}
      {error && !intent ? <Alert severity="error" action={<Button onClick={() => void owners.refetch()}>Refresh</Button>}>{error}</Alert> : null}
    </Stack>
    <Dialog open={Boolean(intent)} onClose={() => !working && setIntent(null)}>
      <DialogTitle>{intent?.kind === "promote" ? `Make ${intent.name} an owner?` : intent?.kind === "primary" ? `Make ${intent.name} the primary billing administrator?` : `Remove ${intent?.name}’s owner role?`}</DialogTitle>
      <DialogContent><Stack spacing={2}>
        <Typography>{intent?.kind === "promote" ? "They will be able to manage this organization, its staff, and company billing. Existing subscriptions and payment methods will not change." : intent?.kind === "primary" ? "This changes the primary billing administrator, not the contracting company or its saved payment methods." : "Their existing staff membership and assigned roles will remain. Company subscriptions and payment methods will not change."}</Typography>
        {error ? <Alert severity="error" action={<Button disabled={working} onClick={() => { setIntent(null); void owners.refetch(); }}>Refresh owners</Button>}>{error}</Alert> : null}
      </Stack></DialogContent>
      <DialogActions><Button disabled={working} onClick={() => setIntent(null)}>Cancel</Button><Button disabled={working} onClick={() => void confirm()}>{working ? "Saving…" : "Confirm"}</Button></DialogActions>
    </Dialog>
  </Paper>;
}
